import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import { Edit, Delete } from "@mui/icons-material";
import { Typography } from "@mui/material";

import messages from "../../../assets/locale/messages";
import userImage from "../../../assets/images/user-profile-image.png";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { renderTextEllipsis, setPageMainTitle } from "../../../utils/Helpers";
import {
  getCategoryDetailsRequest,
  getCategoryDetailsResponse,
  deleteCategoryRequest,
} from "../../../store/Categories/actions";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import EmptyState from "../../../components/EmptyState";
import "./ViewServiceProviderCaregories.scss";

const ViewServiceProviderCategories = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const history = useHistory();

  const lang = useSelector((state) => state.locale.lang);
  const categoryDetails = useSelector(
    (state) => state.categories.categoryDetails
  );
  const { shared, serviceProviderCategories, pagesTitles } = messages[lang];

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const allowMultiSessions = categoryDetails?.is_multi_session_allowed ?? false;

  useEffect(() => {
    setPageMainTitle("categoriesManagement");
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(getCategoryDetailsRequest(id));
    }
    return () => {
      dispatch(getCategoryDetailsResponse({}));
    };
  }, [dispatch, id]);

  const handleDeleteServiceProviderCategory = () => {
    dispatch(
      deleteCategoryRequest({
        id,
      })
    );
    setOpenDeleteConfirm(false);
  };

  return (
    <div className="view-category-page">
      <div className="d-flex mb-4 flex-column flex-md-row">
        <Typography
          variant="h4"
          sx={{ flexGrow: 1 }}
          className={`page-title ${lang === "en" ? "me-5" : "ms-5"}`}
        >
          {categoryDetails
            ? categoryDetails.title_ar || categoryDetails.title_en
              ? lang === "en"
                ? categoryDetails.title_en
                : categoryDetails.title_ar
              : pagesTitles.serviceProviderCategoriesView
            : pagesTitles.serviceProviderCategoriesView}
        </Typography>
        {categoryDetails && (
          <div className="actions-container d-flex justify-content-end mb-4 mt-4 mt-md-0">
            <Link
              to={ROUTE_PATHS.editServiceProviderCategories.replace(":id", id)}
            >
              <Button
                label={
                  <>
                    <Edit />
                    <span className="mx-2">{shared.edit}</span>
                  </>
                }
                className={`fsize-14 text-white btn-secondary ${
                  lang === "en" ? "me-3" : "ms-3"
                }`}
              />
            </Link>
            <Button
              label={
                <>
                  <Delete />
                  <span className="mx-2">{shared.delete}</span>
                </>
              }
              outlined
              className="fsize-14"
              onClick={() => setOpenDeleteConfirm(true)}
            />
          </div>
        )}
      </div>
      <div
        className={`px-2 px-md-5 py-5 bg-white rounded-4 ${
          !categoryDetails && "d-flex justify-content-center"
        }`}
      >
        {categoryDetails ? (
          <div className="view-details-container">
            <div className="row d-flex flex-column-reverse flex-md-row">
              <div className="mb-4 col-10">
                <div className="mb-4">
                  <div className="text-dark text-primary fsize-16 mb-2">
                    {shared.lists.enTitle}
                  </div>
                  <div className="text-gray fsize-18">
                    {categoryDetails.title_en}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-dark text-primary fsize-16 mb-2">
                    {shared.lists.arTitle}
                  </div>
                  <div className="text-gray fsize-18">
                    {categoryDetails.title_ar}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="text-dark text-primary fsize-16 mb-2">
                    {serviceProviderCategories.list.addedSpecialties}
                  </div>
                  {categoryDetails.specialties?.length ? (
                    <div className="text-gray fsize-18">
                      {categoryDetails.specialties?.map((specialty) =>
                        lang === "en" ? (
                          <p className="mb-2" key={specialty.id}>
                            {renderTextEllipsis(specialty.title_en)}
                          </p>
                        ) : (
                          <p className="mb-2" key={specialty.id}>
                            {renderTextEllipsis(specialty.title_ar)}
                          </p>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="text-secondary fsize-18">
                      {serviceProviderCategories.viewCategory.noSpecialty}
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="text-dark text-primary fsize-16 mb-2">
                    {serviceProviderCategories.list.addedServiceProviders}
                  </div>
                  {categoryDetails.has_service_providers ? (
                    <div className="text-gray fsize-18">
                      {categoryDetails.service_providers.map(
                        (serviceProvider) => (
                          <Link
                            to={ROUTE_PATHS.viewServiceProvider.replace(
                              ":id",
                              serviceProvider.id
                            )}
                            key={serviceProvider.id}
                          >
                            {lang === "en" ? (
                              <p className="mb-2">
                                {renderTextEllipsis(serviceProvider.name_en)}
                              </p>
                            ) : (
                              <p className="mb-2">
                                {renderTextEllipsis(serviceProvider.name_ar)}
                              </p>
                            )}
                          </Link>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="text-secondary fsize-18">
                      {serviceProviderCategories.viewCategory.noProvider}
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="text-dark text-primary fsize-16 mb-2">
                    {serviceProviderCategories.viewCategory.allowMultiSessions}
                  </div>
                  <div className="text-gray fsize-18">
                    {allowMultiSessions
                      ? serviceProviderCategories.viewCategory.allowed
                      : serviceProviderCategories.viewCategory.notAllowed}
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-2 mb-2 mb-md-0">
                <div className="text-dark text-primary fsize-16 mb-2">
                  {serviceProviderCategories.addCategory.labels.uploadLogo}
                </div>
                <div
                  style={{
                    backgroundImage: `url(${
                      categoryDetails.image ?? userImage
                    })`,
                  }}
                  className="category-logo"
                ></div>
              </div>
            </div>
          </div>
        ) : (
          <EmptyState
            subTitle={shared.noDataToShow}
            ButtonLabel={shared.back}
            handleBtnAction={() => {
              history.push(ROUTE_PATHS.serviceProviderCategories);
            }}
          />
        )}
      </div>
      <Modal
        open={openDeleteConfirm}
        handleClose={() => setOpenDeleteConfirm(false)}
        title={serviceProviderCategories.deleteCategory.title}
        contentClass="mb-5 pb-2 text-primary"
        content={
          <span>
            {serviceProviderCategories.deleteCategory.deleteConfirmationMsg}
          </span>
        }
        confirmBtnTxt={shared.delete}
        isContentText={false}
        maxWidth="xs"
        cancelBtnTxt={shared.cancel}
        handleConfirm={handleDeleteServiceProviderCategory}
        handleCancel={() => setOpenDeleteConfirm(false)}
        modalClass="delete-categories-modal"
        actionsClass="container px-4"
        confirmBtnClass="btn-secondary fsize-14 text-white py-2 "
        cancelBtnClass="fsize-14 py-2"
      />
    </div>
  );
};

export default ViewServiceProviderCategories;
